import * as React from "react";

import style from "./file-sharing.scss";
import { ROOT_PATH, SharedFile } from "domain/fileSharing";
import { fileSharingService } from "services/file-sharing/FileSharingService";
import { logger } from "utils/logging";

type DragPayload =
    | { isFolder: true; folderPath: string; filename: string; directoryPath: string }
    | { isFolder: false; uuid: string; filename: string; directoryPath: string };

function parseDragPayload(raw: string): DragPayload | undefined {
    try {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null) {
            return undefined;
        }
        const obj = parsed as Record<string, unknown>;
        if (
            typeof obj["isFolder"] !== "boolean" ||
            typeof obj["filename"] !== "string" ||
            typeof obj["directoryPath"] !== "string"
        ) {
            return undefined;
        }
        if (obj["isFolder"] === true) {
            if (typeof obj["folderPath"] !== "string") {
                return undefined;
            }
            return {
                isFolder: true,
                folderPath: obj["folderPath"],
                filename: obj["filename"],
                directoryPath: obj["directoryPath"],
            };
        }
        if (typeof obj["uuid"] !== "string") {
            return undefined;
        }
        return { isFolder: false, uuid: obj["uuid"], filename: obj["filename"], directoryPath: obj["directoryPath"] };
    } catch {
        return undefined;
    }
}

export interface PendingDrop {
    name: string;
    from: string;
    to: string;
    execute: (allowOverride: boolean) => Promise<void>;
}

export function useFileSharingDrop(
    files: SharedFile[],
    onRefresh: () => void,
    onError: (error: Error) => void
): {
    pendingDrop: PendingDrop | undefined;
    setPendingDrop: React.Dispatch<React.SetStateAction<PendingDrop | undefined>>;
    onDrop: (targetPath: string, e: React.DragEvent<HTMLDivElement>) => void;
} {
    const [pendingDrop, setPendingDrop] = React.useState<PendingDrop | undefined>();

    const filesRef = React.useRef(files);
    filesRef.current = files;
    const onRefreshRef = React.useRef(onRefresh);
    onRefreshRef.current = onRefresh;
    const onErrorRef = React.useRef(onError);
    onErrorRef.current = onError;

    const onDrop = React.useCallback((targetPath: string, e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.currentTarget.classList.remove(style.dragOver);
        const raw = e.dataTransfer.getData("application/json");
        if (!raw) {
            return;
        }
        const dragData = parseDragPayload(raw);
        if (!dragData) {
            return;
        }
        if (dragData.isFolder) {
            const sourcePath = dragData.folderPath;
            if (targetPath === sourcePath || targetPath.startsWith(`${sourcePath}/`)) {
                return;
            }
            const folderName = sourcePath.split("/").pop()!;
            const newBase = targetPath ? `${targetPath}/${folderName}` : folderName;
            if (newBase === sourcePath) {
                return;
            }
            const filesToMove = filesRef.current.filter(
                (f) => f.directoryPath === sourcePath || f.directoryPath.startsWith(`${sourcePath}/`)
            );
            setPendingDrop({
                name: folderName,
                from: sourcePath,
                to: newBase,
                // Files are moved with Promise.all — a single failure reports an error but any
                // already-completed moves are not rolled back, leaving the folder partially moved.
                execute: (allowOverride: boolean) =>
                    Promise.all(
                        filesToMove.map((file) => {
                            const suffix = file.directoryPath.slice(sourcePath.length);
                            return fileSharingService.moveFile({
                                uuid: file.uuid,
                                new_path: newBase + suffix,
                                allow_override: allowOverride,
                            });
                        })
                    )
                        .then(() => {
                            onRefreshRef.current();
                        })
                        .catch((error: Error) => {
                            logger.error("Failed to move folder", error);
                            onErrorRef.current(error);
                            // Always refresh so any already-moved files appear in their new location.
                            onRefreshRef.current();
                        }),
            });
        } else {
            if ((dragData.directoryPath || ROOT_PATH) === (targetPath || ROOT_PATH)) {
                return;
            }
            setPendingDrop({
                name: dragData.filename,
                from: dragData.directoryPath || ROOT_PATH,
                to: targetPath || ROOT_PATH,
                execute: (allowOverride: boolean) =>
                    fileSharingService
                        .moveFile({
                            uuid: dragData.uuid,
                            new_path: targetPath || ROOT_PATH,
                            allow_override: allowOverride,
                        })
                        .then(() => onRefreshRef.current())
                        .catch((error: Error) => {
                            logger.error("Failed to move file via drag and drop", error);
                            onErrorRef.current(error);
                        }),
            });
        }
    }, []);

    return { pendingDrop, setPendingDrop, onDrop };
}
